import type { QueueClient } from '../../../types/client';

/**
 * Local recomputation of `scoreLevel`, the CPI band shown on every row.
 *
 * MIRROR — keep in sync with `server/utils/client.js` `getClientBaseLevel`
 * (the tercile half of it). The server computes the band across the WHOLE
 * client list, so `getClientById` cannot: its reducer explicitly copies the
 * previous `scoreLevel` forward ("the endpoint don't compute it",
 * store/client/duck.js:645-647). That is why a client whose policies or income
 * just changed keeps a stale band until the next full `getClient`.
 *
 * Every client already carries `totalScore` (server/utils/client.js:518) —
 * including the single client a by-id refresh brings back — so the band can be
 * recomputed here from data already in the store, with no extra request.
 *
 * Two deliberate guards keep this from ever being *more* generous than the
 * server:
 *  - `CPI_LEAST_VALID_CLIENTS` is a server env var the browser cannot read, so
 *    the recomputation only runs when the server has already handed out at
 *    least one numeric `scoreLevel` — i.e. it already decided the base is
 *    valid. Otherwise the list is returned untouched.
 *  - a valid client with no numeric `totalScore` keeps whatever band the server
 *    gave it and takes no part in the ranking.
 *
 * `clientBaseLevel` is NOT recomputed: its boundaries live in the platform-wide
 * `Figure` document, which the web is never sent (utils/figure.js `getFigure`
 * projects them away), and it moves slowly enough not to matter within a
 * session.
 */
const scoreOf = (client: QueueClient): number | null =>
  typeof client.totalScore === 'number' && !Number.isNaN(client.totalScore) ? client.totalScore : null;

const serverLevelOf = (client: QueueClient): number | null => {
  const level = Number.parseInt(String(client.scoreLevel), 10);
  return Number.isNaN(level) ? null : level;
};

export const withClientScoreLevels = (clients: QueueClient[]): QueueClient[] => {
  const list = clients || [];
  // The server's own verdict on whether this client base can be banded at all.
  if (!list.some(client => client.validScore && serverLevelOf(client) !== null)) return list;

  const ranked = list.filter(client => Boolean(client.validScore) && scoreOf(client) !== null);
  const validCount = ranked.length;
  if (validCount === 0) return list;

  // Same split as the server: three equal buckets, the remainder going to Mid
  // first and then High. (The server also keeps a `highCount`, but its final
  // branch assigns High unconditionally, so it is never read.)
  let lowCount = Math.floor(validCount / 3);
  let midCount = Math.floor(validCount / 3);
  if (validCount % 3 === 1) midCount += 1;
  else if (validCount % 3 === 2) midCount += 1;

  const ascending = [...ranked].sort((left, right) => (scoreOf(left) as number) - (scoreOf(right) as number));
  const levels = new Map<string, number>();
  ascending.forEach(client => {
    let level: number;
    if (lowCount > 0) {
      level = 2;
      lowCount -= 1;
    } else if (midCount > 0) {
      level = 1;
      midCount -= 1;
    } else {
      level = 0;
    }
    levels.set(client._id, level);
  });

  // Original order is preserved, and only the clients whose band actually moved
  // get a new object — everything downstream memoises on client identity.
  let changed = false;
  const next = list.map(client => {
    const level = levels.get(client._id);
    if (level === undefined || serverLevelOf(client) === level) return client;
    changed = true;
    return { ...client, scoreLevel: level };
  });
  return changed ? next : list;
};

/**
 * The platform-wide peer benchmark — `state.figure`, filled once per adviser
 * session by `getFigure()` in the boot sequence (store/user/duck.js:821).
 *
 * `getClientAnalysisScores` needs it to produce `peerScore`, the 排名 axis of
 * the coverage radar (utils/client.js:119-123). This page used to read it from
 * `state.user.figure`, which does not exist — `user` is an Immutable Record
 * with no such field — so `figure` arrived `undefined`, the whole `if (figure)`
 * branch was skipped, and EVERY client's 排名 rendered as "never scored": a
 * grey label and a vertex collapsed to the centre, while the same client on a
 * V1 row (ClientEntry.js:1579 maps `state.figure`) showed a real 排名.
 *
 * The zero guard is not paranoia. figure/duck's initial state is every
 * threshold at 0, and against zeros `getLifeBetterThanKey`/`getCIBetterThanKey`
 * both return '', which `getPeerScore` turns into a hard 0 — a red 「危」 on an
 * axis whose benchmark has merely not arrived yet. Withholding it until the
 * fetch lands keeps the radar's own rule that a missing score is unknown, not a
 * gap (CoverageRadar's UNKNOWN_LABEL_COLOR), and it can never be more generous
 * than V1: once the figures are real, the two pages compute the same number.
 */
export type PeerFigure = {
  average?: number;
  protectionAmountOf25?: number;
  protectionAmountOf50?: number;
  protectionAmountOf75?: number;
  ciProtectionAmountOf25?: number;
  ciProtectionAmountOf50?: number;
  ciProtectionAmountOf75?: number;
};

// The six thresholds the two better-than helpers actually read. Named rather
// than swept up with Object.values so a field added to the duck later cannot
// silently decide this question.
const THRESHOLDS: (keyof PeerFigure)[] = [
  'protectionAmountOf25',
  'protectionAmountOf50',
  'protectionAmountOf75',
  'ciProtectionAmountOf25',
  'ciProtectionAmountOf50',
  'ciProtectionAmountOf75'
];

export const peerFigureOf = (state: { figure?: PeerFigure } | null | undefined): PeerFigure | undefined => {
  const figure = state?.figure;
  if (!figure) return undefined;
  const loaded = THRESHOLDS.some(key => {
    const value = figure[key];
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
  });
  return loaded ? figure : undefined;
};
